import React, { useEffect } from 'react'
import { Empty, Modal, Spin, Tag, Timeline } from 'antd'
import { inject, observer } from 'mobx-react'
import RichText from '../../components/RichEditor/RichText'
import date_format from '../../date_format'
import { ListHistoryLogModalWrapper } from '../ListHistoryLog/ListHistoryLogModalStyled'
import FileItem from '../../components/Common/FileItemComponent/FileItem'
import utils from '../../utils'

const parseSnapshot = value => {
  if (!value) return {}

  try {
    const parsedValue = typeof value === 'string' ? JSON.parse(value) : value
    return parsedValue || {}
  } catch (error) {
    return { content: value }
  }
}

const renderValue = value => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return (
      <span style={{ color: '#999', fontStyle: 'italic' }}>
        Không có dữ liệu
      </span>
    )
  }

  if (Array.isArray(value)) {
    return (
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {value.map(receiver => (
          <li key={receiver}>{utils.getNameInCapitalize(receiver)}</li>
        ))}
      </ul>
    )
  }

  return <RichText htmlText={value} />
}

const renderAttachments = attachments => {
  if (!attachments || attachments.length === 0) {
    return (
      <span style={{ color: '#999', fontStyle: 'italic' }}>
        Không có tài liệu đính kèm
      </span>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {attachments.map(file => (
        <FileItem
          key={file.fileId || file.file_id}
          file_id={file.fileId || file.file_id}
          file_name={file.fileName || file.file_name}
          file_type={utils.getExtensionFile(file.fileName || file.file_name)}
        />
      ))}
    </div>
  )
}

const renderSnapshot = snapshot => {
  const hasContent = Object.prototype.hasOwnProperty.call(snapshot, 'content')
  const hasAttachments = Object.prototype.hasOwnProperty.call(
    snapshot,
    'attachments'
  )
  const hasTitle = Object.prototype.hasOwnProperty.call(snapshot, 'title')
  const hasReceivers = Object.prototype.hasOwnProperty.call(
    snapshot,
    'receivers'
  )

  if (hasContent || hasAttachments) {
    return (
      <div>
        {hasContent && (
          <div>
            {hasAttachments && (
              <strong
                style={{
                  display: 'block',
                  marginBottom: 4,
                  color: '#555',
                }}>
                Nội dung:
              </strong>
            )}
            {renderValue(snapshot.content)}
          </div>
        )}
        {hasAttachments && (
          <div style={{ marginTop: hasContent ? 10 : 0 }}>
            <strong
              style={{
                display: 'block',
                marginBottom: 4,
                color: '#555',
              }}>
              Tài liệu đính kèm:
            </strong>
            {renderAttachments(snapshot.attachments)}
          </div>
        )}
      </div>
    )
  }

  if (hasTitle) {
    return renderValue(snapshot.title)
  }

  if (hasReceivers) {
    return renderValue(snapshot.receivers)
  }

  return renderValue('Không có dữ liệu')
}

const InternalMessageHistoryModal = props => {
  const {
    code,
    isVisible,
    handleCloseHistory,
    listHistoryLogStore,
    title = 'Lịch sử chỉnh sửa tin nhắn',
  } = props
  const { list, isLoading, message: storeMessage } = listHistoryLogStore

  useEffect(() => {
    if (!isVisible || !code) return undefined

    listHistoryLogStore.clear()
    listHistoryLogStore.getList(code)
    return () => listHistoryLogStore.clear()
  }, [code, isVisible, listHistoryLogStore])

  const historyItems = list.filter(
    item => item.document_type === 'INTERNAL_MESSAGE'
  )

  return (
    <ListHistoryLogModalWrapper
      title={title}
      width={1100}
      style={{ top: 32 }}
      visible={isVisible}
      closable={true}
      maskClosable={true}
      onCancel={handleCloseHistory}
      footer={null}>
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin tip="Đang tải lịch sử chỉnh sửa..." />
        </div>
      )}

      {!isLoading && storeMessage && historyItems.length === 0 && (
        <Empty description={storeMessage} />
      )}

      {!isLoading && !storeMessage && historyItems.length === 0 && (
        <Empty description="Chưa có lịch sử chỉnh sửa" />
      )}

      {!isLoading && historyItems.length > 0 && (
        <Timeline>
          {historyItems.map((item, index) => {
            const snapshotBefore = parseSnapshot(item.data_before)
            const snapshotAfter = parseSnapshot(item.data_after)
            const fieldLabels = {
              content: 'Nội dung',
              title: 'Tiêu đề',
              receivers: 'Người nhận',
              attachments: 'Tài liệu đính kèm',
            }
            const changedKeys = Object.keys(snapshotAfter).filter(
              k => fieldLabels[k]
            )
            const changedFieldLabel =
              changedKeys.map(k => fieldLabels[k]).join(', ') || 'Dữ liệu'

            const userName =
              item.user?.name_uppercase || item.user_name || 'Không rõ'
            const loginName = item.user?.user_name || item.user_name

            return (
              <Timeline.Item key={item.id} color="blue">
                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 8 }}>
                    <span
                      style={{
                        color: '#777',
                        fontSize: 12,
                        marginRight: 10,
                      }}>
                      {date_format.HH_mm_DD_MM_YYYY(item.processed_date)}
                    </span>
                    <Tag color="geekblue">
                      Đã chỉnh sửa: {changedFieldLabel}
                    </Tag>
                    <strong>{utils.getNameInCapitalize(userName)}</strong>
                    {loginName && loginName !== userName && (
                      <span style={{ color: '#777', marginLeft: 6 }}>
                        ({loginName})
                      </span>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 16,
                  }}>
                  <div
                    style={{
                      border: '1px solid #ffccc7',
                      borderRadius: 4,
                      background: '#fff2f0',
                      padding: 12,
                      minHeight: 110,
                    }}>
                    <strong style={{ color: '#cf1322' }}>
                      {changedFieldLabel} trước khi chỉnh sửa
                    </strong>
                    <div style={{ marginTop: 10, overflowWrap: 'anywhere' }}>
                      {renderSnapshot(snapshotBefore)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: '1px solid #b7eb8f',
                      borderRadius: 4,
                      background: '#f6ffed',
                      padding: 12,
                      minHeight: 110,
                    }}>
                    <strong style={{ color: '#389e0d' }}>
                      {changedFieldLabel} sau khi chỉnh sửa
                    </strong>
                    <div style={{ marginTop: 10, overflowWrap: 'anywhere' }}>
                      {renderSnapshot(snapshotAfter)}
                    </div>
                  </div>
                </div>
              </Timeline.Item>
            )
          })}
        </Timeline>
      )}
    </ListHistoryLogModalWrapper>
  )
}

export default inject('listHistoryLogStore')(
  observer(InternalMessageHistoryModal)
)
